import { createReducer, on } from '@ngrx/store';
import {
  Attribute,
  AttributeOption,
  AttributesListMeta,
  Gs1AiDefinition,
} from '../models/attribute.model';
import * as AttributesActions from './attributes.actions';
import { ApiTableFilter } from '../../../shared/models/table-filter.model';

export interface AttributesQueryState {
  search?: string;
  filters?: ApiTableFilter[];
  filterLink?: string;
  sort?: string;
  sortDirection?: string;
}

/**
 * Options cache state for an individual attribute.
 */
export interface AttributeOptionsCacheEntry {
  options: AttributeOption[];
  loading: boolean;
  error: string | null;
}

export interface AttributesState {
  attributes: Attribute[];
  meta: AttributesListMeta | null;
  isLoading: boolean;
  isSubmitting: boolean;
  isDeleting: boolean;
  isExporting: boolean;
  gs1AiDefinitions: Gs1AiDefinition[];
  isGs1DefinitionsLoading: boolean;
  /** @deprecated Use optionsCache instead */
  attributeOptions: AttributeOption[];
  /** @deprecated Use optionsCache instead */
  attributeOptionsId: string | null;
  /** @deprecated Use optionsCache instead */
  isOptionsLoading: boolean;
  /** Options cache by attribute UUID - supports multiple concurrent loads */
  optionsCache: Record<string, AttributeOptionsCacheEntry>;
  error: string | null;
  lastExportJobId: string | number | null;
  lastQuery: AttributesQueryState;
}

export const initialAttributesState: AttributesState = {
  attributes: [],
  meta: null,
  isLoading: false,
  isSubmitting: false,
  isDeleting: false,
  isExporting: false,
  gs1AiDefinitions: [],
  isGs1DefinitionsLoading: false,
  attributeOptions: [],
  attributeOptionsId: null,
  isOptionsLoading: false,
  optionsCache: {},
  error: null,
  lastExportJobId: null,
  lastQuery: {},
};

export const attributesReducer = createReducer(
  initialAttributesState,

  on(AttributesActions.loadAttributes, (state, action) => ({
    ...state,
    isLoading: true,
    error: null,
    lastQuery: {
      search: action.search,
      filters: action.filters,
      filterLink: action.filterLink,
      sort: action.sort,
      sortDirection: action.sortDirection,
    },
  })),
  on(AttributesActions.loadAttributesSuccess, (state, { attributes, meta }) => ({
    ...state,
    attributes,
    meta,
    isLoading: false,
  })),
  on(AttributesActions.loadAttributesFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(AttributesActions.submitAttribute, (state) => ({
    ...state,
    isSubmitting: true,
    error: null,
  })),
  on(AttributesActions.submitAttributeSuccess, (state) => ({
    ...state,
    isSubmitting: false,
  })),
  on(AttributesActions.submitAttributeFailure, (state, { error }) => ({
    ...state,
    isSubmitting: false,
    error,
  })),

  on(AttributesActions.deleteAttribute, (state) => ({
    ...state,
    isDeleting: true,
    error: null,
  })),
  on(AttributesActions.deleteAttributeSuccess, (state) => ({
    ...state,
    isDeleting: false,
  })),
  on(AttributesActions.deleteAttributeFailure, (state, { error }) => ({
    ...state,
    isDeleting: false,
    error,
  })),

  on(AttributesActions.exportAttributes, (state) => ({
    ...state,
    isExporting: true,
    error: null,
  })),
  on(AttributesActions.exportAttributesSuccess, (state, { jobId }) => ({
    ...state,
    isExporting: false,
    lastExportJobId: jobId ?? null,
  })),
  on(AttributesActions.exportAttributesFailure, (state, { error }) => ({
    ...state,
    isExporting: false,
    error,
  })),

  on(AttributesActions.loadGs1AiDefinitions, (state) => ({
    ...state,
    isGs1DefinitionsLoading: true,
  })),
  on(AttributesActions.loadGs1AiDefinitionsSuccess, (state, { definitions }) => ({
    ...state,
    gs1AiDefinitions: definitions,
    isGs1DefinitionsLoading: false,
  })),
  on(AttributesActions.loadGs1AiDefinitionsFailure, (state) => ({
    ...state,
    gs1AiDefinitions: [],
    isGs1DefinitionsLoading: false,
  })),

  on(AttributesActions.createGs1Attribute, (state) => ({
    ...state,
    isSubmitting: true,
    error: null,
  })),
  on(AttributesActions.createGs1AttributeSuccess, (state) => ({
    ...state,
    isSubmitting: false,
  })),
  on(AttributesActions.createGs1AttributeFailure, (state, { error }) => ({
    ...state,
    isSubmitting: false,
    error,
  })),

  on(AttributesActions.loadAttributeOptions, (state, { attributeId }) => ({
    ...state,
    attributeOptions:
      state.attributeOptionsId === attributeId ? state.attributeOptions : [],
    attributeOptionsId: attributeId,
    isOptionsLoading: true,
  })),
  on(
    AttributesActions.loadAttributeOptionsSuccess,
    (state, { attributeId, options }) =>
      state.attributeOptionsId === attributeId
        ? { ...state, attributeOptions: options, isOptionsLoading: false }
        : state,
  ),
  on(AttributesActions.loadAttributeOptionsFailure, (state, { error }) => ({
    ...state,
    isOptionsLoading: false,
    error,
  })),
  on(AttributesActions.clearAttributeOptions, (state) => ({
    ...state,
    attributeOptions: [],
    attributeOptionsId: null,
    isOptionsLoading: false,
  })),

  // Cached options reducers
  on(AttributesActions.loadCachedAttributeOptions, (state, { attributeUuid }) => ({
    ...state,
    optionsCache: {
      ...state.optionsCache,
      [attributeUuid]: {
        options: state.optionsCache[attributeUuid]?.options ?? [],
        loading: true,
        error: null,
      },
    },
  })),
  on(AttributesActions.loadCachedAttributeOptionsSuccess, (state, { attributeUuid, options }) => ({
    ...state,
    optionsCache: {
      ...state.optionsCache,
      [attributeUuid]: {
        options,
        loading: false,
        error: null,
      },
    },
  })),
  on(AttributesActions.loadCachedAttributeOptionsFailure, (state, { attributeUuid, error }) => ({
    ...state,
    optionsCache: {
      ...state.optionsCache,
      [attributeUuid]: {
        options: state.optionsCache[attributeUuid]?.options ?? [],
        loading: false,
        error,
      },
    },
  })),
  on(AttributesActions.clearCachedAttributeOptions, (state) => ({
    ...state,
    optionsCache: {},
  })),

  on(AttributesActions.resetAttributesState, () => initialAttributesState),
);
